import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { COURSES } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { buttonClass } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import {
  ChevronRightIcon,
  DownloadIcon,
  FilterIcon,
  PlusIcon,
  SearchIcon,
  TrophyIcon,
} from '../../components/icons';
import { RecipesIcon } from '../../components/NavIcons';
import { SelectField } from '../../components/SelectField';
import { SkeletonCards } from '../../components/Skeleton';
import { RecipeCard } from '../../components/recipes/RecipeCard';
import { courseBadgeClass } from '../../lib/recipeVisuals';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import {
  DEFAULT_FILTERS,
  recipeKeys,
  toListQuery,
  type RecipeFilters,
  type RecipeSort,
} from './queries';
import { CUISINE_OPTIONS, courseLabel, parseCourseParam, parseCuisineParam } from './taxonomy';

const SORTS: ReadonlyArray<{ value: RecipeSort; label: string }> = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'rating', label: 'Highest rated' },
];

type Tab = 'mine' | 'shared';

export function RecipeListPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'shared' ? 'shared' : 'mine';
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<RecipeFilters>(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);
  // Cuisine/course live in the URL so a filtered list can be bookmarked or shared.
  const cuisine = parseCuisineParam(params.get('cuisine'));
  const course = parseCourseParam(params.get('course'));
  const active: RecipeFilters = { ...filters, q: debouncedSearch, cuisine, course };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const scope = tab === 'shared' ? 'public' : 'own';
  const list = useInfiniteQuery({
    queryKey: recipeKeys.list(scope, active),
    queryFn: ({ pageParam }) =>
      scope === 'public'
        ? api.recipes.listPublic(toListQuery(active, { offset: pageParam }))
        : api.recipes.list(toListQuery(active, { offset: pageParam })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.recipes.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
  // The "Browse shared" tab's tag chips come from the shared layer, not this household's tags.
  const tags = useQuery({
    queryKey: tab === 'shared' ? recipeKeys.publicTags : recipeKeys.tags,
    queryFn: () => (tab === 'shared' ? api.recipes.publicTags() : api.recipes.tags()),
  });

  const fork = useMutation({
    mutationFn: (id: string) => api.recipes.fork(id),
    onSuccess: async (recipe) => {
      await qc.invalidateQueries({ queryKey: recipeKeys.all });
      void navigate(`/recipes/${recipe.id}`);
    },
  });

  function setParam(key: 'tab' | 'cuisine' | 'course', value: string) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === '') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }
  function setTab(next: Tab) {
    setParam('tab', next === 'shared' ? 'shared' : '');
  }

  const recipes = list.data?.pages.flatMap((p) => p.recipes) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const tabClass = (isActive: boolean) =>
    `min-h-11 flex-1 rounded-lg px-3 text-sm font-semibold transition-colors ${
      isActive ? 'bg-brand text-white shadow-sm' : 'text-brand hover:bg-brand-soft'
    }`;
  const activeFilterCount =
    (filters.visibility ? 1 : 0) + (cuisine ? 1 : 0) + (course ? 1 : 0) + (filters.tag ? 1 : 0);
  const selectClass = 'min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink';

  return (
    <AppShell title="Recipes" width="wide">
      <div className="flex flex-col gap-4 lg:gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div
            role="group"
            aria-label="Recipe source"
            className="flex gap-1 rounded-xl border border-line bg-surface p-1 sm:w-80"
          >
            <button
              type="button"
              aria-pressed={tab === 'mine'}
              onClick={() => setTab('mine')}
              className={tabClass(tab === 'mine')}
            >
              Our recipes
            </button>
            <button
              type="button"
              aria-pressed={tab === 'shared'}
              onClick={() => setTab('shared')}
              className={tabClass(tab === 'shared')}
            >
              Browse shared
            </button>
          </div>
          <div className="flex gap-2">
            <Link
              to="/recipes/new"
              className={`${buttonClass({ size: 'sm', block: false })} flex-1 whitespace-nowrap sm:flex-none`}
            >
              <PlusIcon className="size-4 max-[399px]:hidden" /> New recipe
            </Link>
            <Link
              to="/recipes/import"
              className={`${buttonClass({ variant: 'secondary', size: 'sm', block: false })} flex-1 whitespace-nowrap sm:flex-none`}
            >
              <DownloadIcon className="size-4 max-[399px]:hidden" /> Import
            </Link>
            <Link
              to="/recipes/rankings"
              className={`${buttonClass({ variant: 'secondary', size: 'sm', block: false })} flex-1 whitespace-nowrap sm:flex-none`}
            >
              <TrophyIcon className="size-4 max-[399px]:hidden" /> Rankings
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <div className="flex flex-col gap-3 lg:sticky lg:top-6 lg:w-72 lg:shrink-0">
            <div className="relative">
              <label htmlFor="recipe-search" className="sr-only">
                Search
              </label>
              <SearchIcon className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted" />
              <input
                id="recipe-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search: chili, sheet pan, garlic…"
                className="min-h-12 w-full rounded-xl border border-line bg-surface pr-3 pl-11 text-base text-ink shadow-sm shadow-brand/5 placeholder:text-muted"
              />
            </div>
            <button
              type="button"
              aria-expanded={filtersOpen}
              aria-controls="recipe-filters"
              onClick={() => setFiltersOpen((o) => !o)}
              className="inline-flex min-h-11 items-center justify-between gap-2 rounded-xl border border-line bg-surface px-4 text-sm font-semibold text-brand hover:bg-brand-soft lg:hidden"
            >
              <span className="inline-flex items-center gap-2">
                <FilterIcon className="size-4" /> Sort &amp; filter
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-orange px-2 py-0.5 text-xs text-brand-dark">
                    {activeFilterCount}
                  </span>
                ) : null}
              </span>
              <span
                aria-hidden
                className={`transition-transform ${filtersOpen ? 'rotate-90' : ''}`}
              >
                <ChevronRightIcon className="size-4" />
              </span>
            </button>

            <div
              id="recipe-filters"
              className={`card flex-col gap-4 p-4 ${filtersOpen ? 'flex' : 'hidden'} lg:flex`}
            >
              <div className="flex flex-col gap-1">
                <label htmlFor="recipe-sort" className="text-sm font-medium text-ink">
                  Sort
                </label>
                <select
                  id="recipe-sort"
                  value={filters.sort}
                  onChange={(e) => setFilters({ ...filters, sort: e.target.value as RecipeSort })}
                  className={selectClass}
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              {tab === 'mine' ? (
                <div className="flex flex-col gap-1">
                  <label htmlFor="recipe-visibility" className="text-sm font-medium text-ink">
                    Visibility
                  </label>
                  <select
                    id="recipe-visibility"
                    value={filters.visibility}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        visibility: e.target.value as RecipeFilters['visibility'],
                      })
                    }
                    className={selectClass}
                  >
                    <option value="">All</option>
                    <option value="private">Private</option>
                    <option value="public">Shared</option>
                  </select>
                </div>
              ) : null}
              <SelectField
                id="recipe-cuisine"
                label="Cuisine"
                value={cuisine}
                options={CUISINE_OPTIONS}
                onChange={(e) => setParam('cuisine', e.target.value)}
              />

              <div className="flex flex-col gap-1.5">
                <span id="course-filter-label" className="text-sm font-medium text-ink">
                  Course
                </span>
                <ul aria-labelledby="course-filter-label" className="flex flex-wrap gap-2">
                  {COURSES.map((key) => {
                    const on = course === key;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          aria-pressed={on}
                          onClick={() => setParam('course', on ? '' : key)}
                          className={`min-h-9 rounded-full px-3 text-sm transition-colors ${
                            on
                              ? `${courseBadgeClass(key)} font-semibold shadow-sm ring-2 ring-brand/30`
                              : 'border border-line bg-surface text-brand hover:border-brand/40 hover:bg-brand-soft'
                          }`}
                        >
                          {courseLabel(key)}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {tags.data && tags.data.tags.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <span id="tag-filter-label" className="text-sm font-medium text-ink">
                    Tags
                  </span>
                  <ul
                    aria-labelledby="tag-filter-label"
                    className="flex max-h-64 flex-wrap gap-2 overflow-y-auto"
                  >
                    {tags.data.tags.map(({ tag, count }) => {
                      const on = filters.tag === tag;
                      return (
                        <li key={tag}>
                          <button
                            type="button"
                            aria-pressed={on}
                            onClick={() => setFilters({ ...filters, tag: on ? '' : tag })}
                            className={`min-h-9 rounded-full px-3 text-sm transition-colors ${
                              on
                                ? 'bg-brand text-white'
                                : 'border border-line bg-surface text-muted hover:bg-brand-soft hover:text-brand'
                            }`}
                          >
                            {tag} <span className="opacity-70">{count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {list.isError ? <Alert tone="error">{messageForError(list.error)}</Alert> : null}
            {fork.isError ? <Alert tone="error">{messageForError(fork.error)}</Alert> : null}

            <p className="text-sm font-medium text-muted" aria-live="polite">
              {list.isPending
                ? 'Loading recipes…'
                : `${total} ${total === 1 ? 'recipe' : 'recipes'}`}
            </p>

            {list.isPending ? (
              <SkeletonCards
                count={6}
                label="Loading recipes"
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
              />
            ) : null}

            {!list.isPending && recipes.length === 0 ? (
              <div className="card">
                <EmptyState
                  accent="orange"
                  icon={<RecipesIcon className="size-full" />}
                  title={
                    tab === 'shared' ? 'No shared recipes match that yet' : 'No recipes here yet'
                  }
                  action={
                    tab === 'shared' ? undefined : (
                      <Link to="/recipes/new" className={buttonClass({ block: false })}>
                        <PlusIcon className="size-4" /> Add a recipe
                      </Link>
                    )
                  }
                >
                  {tab === 'shared'
                    ? 'Try another search, or clear a filter.'
                    : 'Add one by hand, or import one from a website.'}
                </EmptyState>
              </div>
            ) : null}

            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {recipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  action={
                    tab === 'shared' && !recipe.own ? (
                      <button
                        type="button"
                        onClick={() => fork.mutate(recipe.id)}
                        disabled={fork.isPending}
                        className={buttonClass({ variant: 'ghost', size: 'sm' })}
                      >
                        Copy to our recipes
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </ul>

            {list.hasNextPage ? (
              <button
                type="button"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                className={`${buttonClass({ variant: 'secondary', block: false })} self-center`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
